import React, { useState, useEffect } from "react";
import {
  Typography,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Chip,
  Divider,
  LinearProgress,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  People as PeopleIcon,
  Event as EventIcon,
  SportsScore as SportsIcon,
  TrendingUp as TrendingUpIcon,
  EmojiEvents as TrophyIcon,
  CachedOutlined as CachedIcon,
} from "@mui/icons-material";
import {
  OverviewStats,
  DivisionStats,
  AllDivisionsOverview,
  Division,
} from "../types";
import { apiRequest } from "../config/api";
import { useDivision } from "../contexts/DivisionContext";
import { useSeason } from "../contexts/SeasonContext";
import { useOrganization } from "../contexts/OrganizationContext";
import DivisionSelector from "../components/DivisionSelector";
import SeasonSelector from "../components/SeasonSelector";

function Dashboard() {
  const { currentDivision, setCurrentDivision } = useDivision();
  const { selectedSeason } = useSeason();
  const { currentOrg } = useOrganization();
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [allDivisionsData, setAllDivisionsData] =
    useState<AllDivisionsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAllDivisions, setShowAllDivisions] = useState(true);
  const [cacheClearLoading, setCacheClearLoading] = useState(false);
  const [cacheSnackbar, setCacheSnackbar] = useState<{ open: boolean; success: boolean; message: string }>({
    open: false,
    success: true,
    message: "",
  });

  const handleClearCache = async () => {
    setCacheClearLoading(true);
    try {
      const response = await apiRequest("/api/admin/cache/clear", { method: "POST" });
      if (response.ok) {
        setCacheSnackbar({ open: true, success: true, message: "Cache cleared — all pages will now load fresh data." });
      } else {
        setCacheSnackbar({ open: true, success: false, message: "Failed to clear cache. Please try again." });
      }
    } catch {
      setCacheSnackbar({ open: true, success: false, message: "Error connecting to server." });
    } finally {
      setCacheClearLoading(false);
    }
  };

  useEffect(() => {
    console.log(
      "[Dashboard] useEffect triggered - currentDivision:",
      currentDivision,
      "showAllDivisions:",
      showAllDivisions,
      "selectedSeason:",
      selectedSeason,
    );
    fetchStats();
  }, [currentDivision, showAllDivisions, selectedSeason]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      console.log(
        "[Dashboard] Fetching stats - showAllDivisions:",
        showAllDivisions,
        "currentDivision:",
        currentDivision,
      );

      if (showAllDivisions) {
        // Fetch overall summary of all divisions
        console.log("[Dashboard] Fetching all divisions overview");
        const url = selectedSeason
          ? `/api/stats/all-divisions?seasonId=${selectedSeason.id}`
          : `/api/stats/all-divisions`;
        const response = await apiRequest(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log("[Dashboard] All divisions data received:", data);
          setAllDivisionsData(data);
          setStats(data.totalStats);
        } else {
          console.error(
            "[Dashboard] Failed to fetch all divisions data:",
            response.status,
          );
        }
      } else {
        // Fetch stats for specific division
        console.log(
          "[Dashboard] Fetching division-specific stats for:",
          currentDivision,
        );
        const url = selectedSeason
          ? `/api/stats/division/${currentDivision}?seasonId=${selectedSeason.id}`
          : `/api/stats/division/${currentDivision}`;
        const response = await apiRequest(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log("[Dashboard] Division-specific data received:", data);
          setStats(data);
        } else {
          console.error(
            "[Dashboard] Failed to fetch division data:",
            response.status,
          );
        }
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDivisionToggle = (viewAll: boolean) => {
    console.log(
      "[Dashboard] Division toggle - current:",
      showAllDivisions,
      "new:",
      viewAll,
    );
    setShowAllDivisions(viewAll);
    // Don't call fetchStats here - the useEffect will handle it when showAllDivisions changes
  };

  const getDivisionColor = (division: string) => {
    switch (division) {
      case "Master":
        return "#d32f2f";
      case "Division 1":
        return "#1976d2";
      case "Division 2":
        return "#388e3c";
      case "Division 2A":
        return "#b5651d";
      case "Division 2B":
        return "#a0522d";
      case "Division 3":
        return "#f57c00";
      case "Division 4":
        return "#795548";
      case "Women Division":
        return "#e91e63";
      case "Women Division 2":
        return "#f06292";
      default:
        return "#757575";
    }
  };

  const getDivisionIcon = (division: string) => {
    switch (division) {
      case "Master":
        return "🏆";
      case "Division 1":
        return "🥇";
      case "Division 2":
        return "🥈";
      case "Division 2A":
        return "🥈";
      case "Division 2B":
        return "🥈";
      case "Division 3":
        return "🥉";
      case "Division 4":
        return "🎾";
      case "Women Division":
        return "👩";
      case "Women Division 2":
        return "👩";
      default:
        return "🎾";
    }
  };

  const getDivisionDisplayName = (division: string): string => {
    return division === "Women Division" ? "Women Division 1" : division;
  };

  // Check if this is a tournament-based organization
  const isTournamentOrg = currentOrg?.competitionType === "tournament";
  const divisionLabel = isTournamentOrg ? "Level" : "Division";
  const divisionsLabel = isTournamentOrg ? "Levels" : "Divisions";
  const eventLabel = isTournamentOrg ? "Tournament" : "League Day";
  const eventsLabel = isTournamentOrg ? "Tournaments" : "League Days";

  const statCards = [
    {
      title: showAllDivisions ? "Total Players" : "Players",
      value: stats?.totalPlayers || 0,
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: "#1976d2",
    },
    {
      title: showAllDivisions ? `Total ${eventsLabel}` : eventsLabel,
      value: stats?.totalLeagueDays || 0,
      icon: <EventIcon sx={{ fontSize: 40 }} />,
      color: "#388e3c",
    },
    {
      title: showAllDivisions ? "Total Matches" : "Matches",
      value: stats?.totalMatches || 0,
      icon: <SportsIcon sx={{ fontSize: 40 }} />,
      color: "#f57c00",
    },
    {
      title: "Completion Rate",
      value: `${stats?.completionRate || 0}%`,
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      color: "#7b1fa2",
    },
  ];

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 2,
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography variant="h4">Dashboard</Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", minWidth: { xs: "100%", sm: "auto" } }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={cacheClearLoading ? <CircularProgress size={14} /> : <CachedIcon />}
            onClick={handleClearCache}
            disabled={cacheClearLoading}
          >
            Clear Cache
          </Button>
          <Box sx={{ minWidth: 200 }}>
            <SeasonSelector excludeDrafts={false} allowAllSeasons={true} />
          </Box>
        </Box>
      </Box>

      {/* Division Control */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: "1px solid rgba(25, 118, 210, 0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 1, sm: 2 },
              width: { xs: "100%", md: "auto" },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "#1976d2",
                whiteSpace: "nowrap",
              }}
            >
              📊 Dashboard View:
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Chip
                label={`All ${divisionsLabel} Summary`}
                onClick={() => handleDivisionToggle(true)}
                color={showAllDivisions ? "primary" : "default"}
                variant={showAllDivisions ? "filled" : "outlined"}
                size="small"
              />
              <Chip
                label={`Specific ${divisionLabel}`}
                onClick={() => handleDivisionToggle(false)}
                color={!showAllDivisions ? "primary" : "default"}
                variant={!showAllDivisions ? "filled" : "outlined"}
                size="small"
              />
            </Box>
          </Box>
          {!showAllDivisions && (
            <Box
              sx={{
                width: { xs: "100%", md: "auto" },
                minWidth: { md: 200 },
                maxWidth: { xs: "100%", md: 250 },
              }}
            >
              <DivisionSelector />
            </Box>
          )}
        </Box>
      </Paper>

      <Typography variant="body1" color="text.secondary" paragraph>
        {showAllDivisions
          ? `Overview of all divisions${selectedSeason ? ` - ${selectedSeason.name}` : " - All Seasons (Unique Players)"}`
          : `Detailed view of ${currentDivision}${selectedSeason ? ` - ${selectedSeason.name}` : " - All Seasons"}`}
      </Typography>

      {/* Stats Cards */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 4 }}>
        {statCards.map((card, index) => (
          <Box key={index} sx={{ flex: "1 1 200px", minWidth: "200px" }}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography color="text.secondary" gutterBottom>
                      {card.title}
                    </Typography>
                    <Typography variant="h4" component="div">
                      {card.value}
                    </Typography>
                  </Box>
                  <Box sx={{ color: card.color }}>{card.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* All Divisions Overview */}
      {showAllDivisions &&
        allDivisionsData &&
        allDivisionsData.divisionBreakdown && (
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <TrophyIcon /> {divisionLabel} Breakdown
            </Typography>
            <Grid container spacing={2}>
              {allDivisionsData &&
                allDivisionsData.divisionBreakdown &&
                allDivisionsData.divisionBreakdown.map(
                  (divisionData, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      key={divisionData.division}
                    >
                      <Card
                        sx={{
                          height: "100%",
                          border: `2px solid ${getDivisionColor(
                            divisionData.division,
                          )}`,
                          borderRadius: 2,
                        }}
                      >
                        <CardContent>
                          <Box sx={{ textAlign: "center", mb: 2 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                color: getDivisionColor(divisionData.division),
                                fontWeight: "bold",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                              }}
                            >
                              {getDivisionIcon(divisionData.division)}{" "}
                              {getDivisionDisplayName(divisionData.division)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Players:
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {divisionData.totalPlayers}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                League Days:
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {divisionData.totalLeagueDays}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Matches:
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {divisionData.totalMatches}
                              </Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Completion:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {divisionData.completionRate}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={divisionData.completionRate}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: "rgba(0,0,0,0.1)",
                                  "& .MuiLinearProgress-bar": {
                                    backgroundColor: getDivisionColor(
                                      divisionData.division,
                                    ),
                                  },
                                }}
                              />
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ),
                )}
            </Grid>
          </Box>
        )}

      {/* Additional Info Sections */}

      <Snackbar
        open={cacheSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setCacheSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={cacheSnackbar.success ? "success" : "error"}
          onClose={() => setCacheSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {cacheSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Dashboard;
